import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import { PageHeader, Card, Btn, Alert, colors, spacing, typography, radius, inputStyle, selectStyle } from '../design-system';

function FieldLabel({ children }) {
  return (
    <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: 6 }}>
      {children}
    </div>
  );
}

function StatusDot({ ok }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold,
      color: ok ? '#059669' : '#94a3b8',
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: ok ? '#10b981' : '#d1d5db', display: 'inline-block' }} />
      {ok ? 'SMTP Configured' : 'Not Configured'}
    </span>
  );
}

const PROVIDER_PRESETS = {
  custom:   { host: '',                  port: 587, use_tls: true },
  gmail:    { host: 'smtp.gmail.com',    port: 587, use_tls: true },
  outlook:  { host: 'smtp.office365.com', port: 587, use_tls: true },
  sendgrid: { host: 'smtp.sendgrid.net', port: 587, use_tls: true },
  mailgun:  { host: 'smtp.mailgun.org',  port: 587, use_tls: true },
};

export default function EmailSettings() {
  const [config,  setConfig]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo,  setTestTo]  = useState('');
  const [msg,     setMsg]     = useState(null);
  const [provider, setProvider] = useState('custom');
  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 5000); };

  useEffect(() => {
    axiosClient.get('/email-settings/')
      .then(r => setConfig(r.data))
      .catch(() => flash('Could not load email settings.', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  const applyPreset = (p) => {
    setProvider(p);
    const preset = PROVIDER_PRESETS[p];
    setConfig(c => ({ ...c, smtp_host: preset.host, smtp_port: preset.port, smtp_use_tls: preset.use_tls }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axiosClient.patch('/email-settings/', config);
      flash('Settings saved.');
    } catch {
      flash('Save failed.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) return;
    setTesting(true);
    try {
      await axiosClient.post('/email-settings/test', { to_email: testTo });
      flash(`Test email sent to ${testTo}.`);
    } catch (err) {
      flash(err?.response?.data?.detail || 'Test email failed.', 'error');
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div style={{ padding: spacing.xl, color: colors.textMuted }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 780, margin: '0 auto', padding: spacing.lg }}>
      <PageHeader
        title="Email Settings"
        subtitle="Configure SMTP for automated notifications — renewal reminders, CAPA assignments, application updates"
      />

      {msg && <Alert type={msg.type} message={msg.text} />}

      {/* Status */}
      <Card style={{ marginBottom: spacing.lg, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <StatusDot ok={config?.configured} />
        <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>
          {config?.configured ? `Sending from ${config.smtp_from_email}` : 'Fill in the form below to enable email notifications.'}
        </div>
      </Card>

      <Card style={{ marginBottom: spacing.lg }}>
        <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, marginBottom: spacing.lg }}>
          SMTP Configuration
        </div>

        {/* Provider quick-select */}
        <div style={{ marginBottom: spacing.lg }}>
          <FieldLabel>Provider Preset</FieldLabel>
          <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
            {Object.keys(PROVIDER_PRESETS).map(p => (
              <button
                key={p}
                onClick={() => applyPreset(p)}
                style={{
                  padding: '6px 14px',
                  borderRadius: radius.pill,
                  border: `1px solid ${provider === p ? colors.primary : colors.border}`,
                  background: provider === p ? colors.primary : colors.surface,
                  color: provider === p ? '#fff' : colors.textSecondary,
                  cursor: 'pointer',
                  fontSize: typography.fontSizes.sm,
                  fontWeight: provider === p ? typography.fontWeights.semibold : typography.fontWeights.medium,
                  textTransform: 'capitalize',
                  transition: 'all 120ms',
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: spacing.md }}>
            <div>
              <FieldLabel>SMTP Host</FieldLabel>
              <input style={inputStyle} value={config?.smtp_host || ''} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.example.com" />
            </div>
            <div>
              <FieldLabel>Port</FieldLabel>
              <input style={inputStyle} type="number" value={config?.smtp_port || 587} onChange={e => set('smtp_port', parseInt(e.target.value))} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
            <div>
              <FieldLabel>Username</FieldLabel>
              <input style={inputStyle} value={config?.smtp_username || ''} onChange={e => set('smtp_username', e.target.value)} autoComplete="off" />
            </div>
            <div>
              <FieldLabel>Password / App Password</FieldLabel>
              <input style={inputStyle} type="password" value={config?.smtp_password || ''} onChange={e => set('smtp_password', e.target.value)} autoComplete="new-password" placeholder="●●●●●●●●" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
            <div>
              <FieldLabel>From Email</FieldLabel>
              <input style={inputStyle} type="email" value={config?.smtp_from_email || ''} onChange={e => set('smtp_from_email', e.target.value)} placeholder="noreply@yourdomain.com" />
            </div>
            <div>
              <FieldLabel>From Name</FieldLabel>
              <input style={inputStyle} value={config?.smtp_from_name || ''} onChange={e => set('smtp_from_name', e.target.value)} placeholder="ISO BOSS" />
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, cursor: 'pointer', fontSize: typography.fontSizes.md, color: colors.textPrimary }}>
            <input type="checkbox" checked={!!config?.smtp_use_tls} onChange={e => set('smtp_use_tls', e.target.checked)} />
            Use STARTTLS (recommended)
          </label>

          <div>
            <Btn variant="primary" size="md" onClick={save} loading={saving}>Save Settings</Btn>
          </div>
        </div>
      </Card>

      {/* Test email */}
      {config?.configured && (
        <Card>
          <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, marginBottom: spacing.md }}>
            Send Test Email
          </div>
          <div style={{ display: 'flex', gap: spacing.sm }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              type="email"
              value={testTo}
              onChange={e => setTestTo(e.target.value)}
              placeholder="recipient@example.com"
            />
            <Btn variant="secondary" size="md" onClick={sendTest} loading={testing} disabled={!testTo.trim()}>
              Send Test
            </Btn>
          </div>
          <div style={{ marginTop: spacing.sm, fontSize: typography.fontSizes.xs, color: colors.textMuted }}>
            Sends a test message to verify your SMTP settings are working.
          </div>
        </Card>
      )}

      {/* Notification triggers reference */}
      <Card style={{ marginTop: spacing.lg }}>
        <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, marginBottom: spacing.md }}>
          Automated Notification Triggers
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {[
            ['Renewal reminder',      'Sent at 90, 60, and 30 days before renewal due date'],
            ['CAPA assigned',         'Sent when a CAPA is assigned to a user'],
            ['Application update',    'Sent when the status of a CB application changes'],
            ['Remote audit invite',   'Sent when a remote audit session is scheduled'],
          ].map(([trigger, desc], i, arr) => (
            <div key={trigger} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: `${spacing.sm} 0`,
              borderBottom: i < arr.length - 1 ? `1px solid ${colors.border}` : 'none',
              fontSize: typography.fontSizes.sm,
            }}>
              <span style={{ fontWeight: typography.fontWeights.medium, color: colors.textPrimary }}>{trigger}</span>
              <span style={{ color: colors.textMuted, maxWidth: 320, textAlign: 'right' }}>{desc}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
